import {
  TGetProductContractHandoffReqDto,
  TGetProductContractHandoffResDto,
} from "@/api/services/admin/product-contract/dtos/get-product-contract-handoff.dto";
import { EOrganizationType } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { CONTRACT_TOKEN_KEY } from "@/utils/constants";
import { createUrlWithParams } from "@/utils/url";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";

export const getProductContractHandoffApi = async (req: TGetProductContractHandoffReqDto): Promise<TGetProductContractHandoffResDto> => {
  const url = createUrlWithParams("/product-contracts/handoff", req);
  const rs = await clientApi.get<TGetProductContractHandoffResDto>(url);
  return rs.data;
};

/**
 * Resolves the product contract handed off by the Center under `oauth:contract:<token>`.
 * The contract token is persisted by the OAuth callback into the `contract-token` cookie; an
 * explicit `token` argument overrides it (e.g. deep-linking with the token in hand).
 */
export const useProductContractHandoff = (token?: string | null) => {
  const contractToken = token ?? Cookies.get(CONTRACT_TOKEN_KEY);

  const query = useQuery({
    queryKey: [QUERY_KEYS.PRODUCT_CONTRACT.GET_HANDOFF, contractToken],
    queryFn: () => getProductContractHandoffApi({ token: contractToken as string }),
    enabled: Boolean(contractToken),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 0,
  });

  const isSigned: boolean | null | undefined = contractToken ? query.data?.data.isSigned : null;
  const contract = query.data?.data.data ?? null;

  let canCreateRso = contract?.organizationType === EOrganizationType.RSO;
  let canCreateCompany = contract?.organizationType === EOrganizationType.COMPANY;

  if (contract?.organizationType === EOrganizationType.OTHER) {
    canCreateRso = true;
    canCreateCompany = true;
  }

  if (isSigned) {
    canCreateRso = false;
    canCreateCompany = false;
  }

  return { ...query, isSigned, contract, canCreateRso, canCreateCompany };
};
